/**
 * Firestore path of a structure field on the triggering element.
 * Structure fields are declared with a bare key (`Adr_field299`) but stored
 * under `data.*`, while standard fields (name, status…) live at the root.
 * Same rule as DataTablesModal / renderPDF.
 */
export function toElementFieldPath(field) {
  const key = (field?.value || '').trim();
  if (!key) return '';
  if (field?.type !== 'data') return key;
  if (key.startsWith('data.') || key.startsWith('values.')) return key;
  return `data.${key}`;
}

/**
 * Build variable tokens exposed by steps before the current one.
 * Used in automation config UI (updateField, conditions, etc.).
 */
export function getAutomationStepVariables(steps, currentStepId) {
  if (!Array.isArray(steps) || !currentStepId) return [];

  const currentIndex = steps.findIndex((s) => s.id === currentStepId);
  if (currentIndex <= 0) return [];

  const result = [];
  for (let i = 0; i < currentIndex; i += 1) {
    const step = steps[i];
    const outputVars = step?.outputVariables || {};
    const stepName = step?.name || `Step ${i + 1}`;

    Object.entries(outputVars).forEach(([varName, mappedPath]) => {
      result.push({
        token: `$${varName}`,
        stepName,
        mappedPath,
      });

      if (mappedPath === 'selected') {
        result.push({
          token: `$${varName}.documentPath`,
          stepName,
          mappedPath: 'selected.documentPath',
        });
        result.push({
          token: `$${varName}.id`,
          stepName,
          mappedPath: 'selected.id',
        });
        const connectorId = step?.type === 'queryCell' ? step.config?.connectorId : '';
        if (connectorId) {
          result.push({
            token: `$${varName}.${connectorId}.id`,
            stepName,
            mappedPath: `selected.${connectorId}.id`,
          });
        }
      }
    });
  }

  return result;
}

/**
 * Trigger element fields as plain paths for step execution conditions.
 * Example: data.Typ_drop719 (not {{...}}).
 */
export function getAutomationConditionFieldPaths(fields) {
  if (!Array.isArray(fields)) return [];

  return fields
    .map((f) => {
      const key = (f.value || '').trim();
      if (!key) return null;
      return {
        token: key,
        label: f.name || key,
      };
    })
    .filter(Boolean);
}

/**
 * Condition variables use plain names (no $ prefix): lastItemCount, lastItem.type
 */
export function getAutomationConditionVariables(steps, currentStepId) {
  if (!Array.isArray(steps) || !currentStepId) return [];

  const currentIndex = steps.findIndex((s) => s.id === currentStepId);
  if (currentIndex <= 0) return [];

  const result = [];
  for (let i = 0; i < currentIndex; i += 1) {
    const step = steps[i];
    const outputVars = step?.outputVariables || {};
    const stepName = step?.name || `Step ${i + 1}`;

    Object.keys(outputVars).forEach((varName) => {
      result.push({ token: varName, stepName });
      if (outputVars[varName] === 'selected') {
        result.push({ token: `${varName}.documentPath`, stepName });
        result.push({ token: `${varName}.id`, stepName });
        const connectorId = step?.type === 'queryCell' ? step.config?.connectorId : '';
        if (connectorId) {
          result.push({ token: `${varName}.${connectorId}.id`, stepName });
        }
      }
    });
  }

  return result;
}

/**
 * Trigger element fields as {{field}} tokens.
 */
export function getTriggerFieldVariables(fields) {
  if (!Array.isArray(fields)) return [];

  return fields
    .map((f) => {
      const key = toElementFieldPath(f);
      if (!key) return null;
      return {
        token: `{{${key}}}`,
        label: f.name || key,
      };
    })
    .filter(Boolean);
}

/**
 * Returns an i18n key when targetElement is invalid, or null if valid/empty.
 */
export function getTargetElementValidationError(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('$')) return 'targetElementMustStartWithDollar';
  return null;
}

/**
 * True if all updateField steps have a valid targetElement (or none).
 */
export function hasInvalidAutomationTargetElements(steps) {
  if (!Array.isArray(steps)) return false;
  return steps.some(
    (step) =>
      step?.type === 'updateField' &&
      getTargetElementValidationError(step?.config?.targetElement)
  );
}
